import { Divider, H2, H3, Section, CheckList } from "./common-components";

function getMedicalConditions(patient) {
  const raw = patient?.medicalHistoryInfo?.medicalConditions;
  if (Array.isArray(raw)) return raw;
  if (raw?.medicalConditions && Array.isArray(raw.medicalConditions)) return raw.medicalConditions;
  return [];
}

function getActiveConditionNames(patient) {
  const conditions = getMedicalConditions(patient);
  const active = conditions.filter((c) => c.status === "active" || c.status == null);
  return active.map((c) => (c.conditionName || c.name || "").toLowerCase());
}

export const ClinicalNeedPharmacistCheckList = ({
  formType,
  patient,
  preAssessment = {},
  medications = {},
  patientMedications = [],
  clinicalAssessment = {},
  clinicalServiceName = "",
}) => {
  const conditionNames = getActiveConditionNames(patient ?? {});
  const reasonsForAMR = preAssessment?.reasonsForMedicationReview ?? [];
  const medReviews = medications?.medications ?? [];

  const reviewMedIds = new Set(medReviews.map((m) => m.medicationId));
  const maintenanceMeds = (patientMedications ?? []).filter(
    (pm) => reviewMedIds.has(pm.id) && !pm.PRN && !pm.prn
  );

  const takingMultipleMaintenance = maintenanceMeds.length >= 3;
  const oneOrMoreChronicDiseases = conditionNames.length > 1;
  const educationAutomatic = true;
  const clinicalServiceLower = (clinicalServiceName ?? "").toLowerCase();
  const isAnnualMedicationReview =
    clinicalServiceLower.includes("medication") || clinicalServiceLower === "medicationreview";

  const hasAdherenceRisk =
    (clinicalAssessment?.routine !== "No" && clinicalAssessment?.routineType === "Usually") ||
    Boolean(clinicalAssessment?.compliance);
  const lateRefill = (clinicalAssessment?.refill ?? "").toLowerCase() === "late";
  const adherenceOrComplianceIdentified =
    Boolean(patient?.isMedicationAdherence) || hasAdherenceRisk || lateRefill;

  const additionOfNewMeds = false;

  const effectivenessAssessed = medReviews.some(
    (m) =>
      String(m.effectiveness ?? m.working ?? "").trim() !== "" &&
      (m.effectiveness ?? m.working) != null
  );
  const hasIntervention = medReviews.some(
    (m) =>
      m.intervention &&
      (m.intervention.medicationName ?? m.intervention.whoChangedMedication ?? m.intervention.override)
  );
  const monitorAndManagement = effectivenessAssessed || hasIntervention;

  const actionPlanLabels = medReviews.flatMap((m) =>
    (m.intervention?.actionPlan ?? []).map((a) => (typeof a === "string" ? a : (a?.label ?? "")))
  );
  const additionalMonitoringRecommended = actionPlanLabels.some((ap) =>
    (ap ?? "").toLowerCase().includes("initiate monitor")
  );

  const reasons = [
    { label: "Taking multiple maintenance medications", checked: takingMultipleMaintenance },
    { label: "One or more chronic diseases /conditions", checked: oneOrMoreChronicDiseases },
    { label: "Education: general information / medications / chronic conditions", checked: educationAutomatic },
    { label: "Adherence or compliance identified", checked: adherenceOrComplianceIdentified },
    { label: "Addition of new medications", checked: additionOfNewMeds },
    { label: "Assess medication effectiveness / tolerance", checked: effectivenessAssessed },
    { label: "Monitor & management for effectiveness / tolerance / - adverse effects", checked: monitorAndManagement },
    { label: "Additional monitoring of medication recommended", checked: additionalMonitoringRecommended },
    { label: "Annual Medication review", checked: isAnnualMedicationReview },
  ];

  const dischargedFromHospital = reasonsForAMR.some((x) =>
    (x ?? "").includes("I had a recent discharge from hospital.")
  );
  const plannedHospitalAdmission = reasonsForAMR.some((x) =>
    (x ?? "").includes("I have a planned upcoming hospital admission.")
  );
  const pharmacistDocumentedDecision = false;
  const physicianNpRequests = false;

  const followUps = [
    { label: "Follow up: Patient is discharged from hospital", checked: dischargedFromHospital },
    { label: "Follow up: Pharmacist's documented decision based on outlined criteria", checked: pharmacistDocumentedDecision },
    { label: "Follow up: A physician or nurse practitioner requests a MedsCheck Follow-up", checked: physicianNpRequests },
    { label: "Follow up: A planned hospital admission", checked: plannedHospitalAdmission },
  ];

  return (
    <Section formType={formType}>
      <H2>Clinical Need For Service - Pharmacist Reasons</H2>
      <div className="flex flex-col gap-1">
        <CheckList list={reasons} />
      </div>
      <Divider />
      <H3 className="!text-teal-300">Follow Up Medication Reviews Coming Soon</H3>
      <div className="flex flex-col gap-1">
        <CheckList list={followUps} />
      </div>
    </Section>
  );
};
